/**
 * terrainProjection - Schattenpunkte auf die Terrain-Oberfläche werfen (#631)
 *
 * Reine, WebGL-/Fabric-freie Geometrie (Jest-testbar). Ersetzt die flache
 * Planebenen-Projektion des shadowCaster durch First-Hit gegen `elevationAt`
 * (#623): Ein Silhouetten-Punkt in absoluter Höhe yAbs wirft seinen Schatten
 * dorthin, wo der Sonnenstrahl durch ihn ZUM ERSTEN MAL die Oberfläche
 * schneidet — Vorwärts-Marsch + Bisektion. Kein Fixpunkt-Verfahren: das fände
 * bei einem aufragenden Plateau zwischen Caster und Schattenende die falsche
 * Wurzel (Strahl wird schon an der Plateau-Flanke abgefangen).
 *
 * Der Strahl fällt monoton (Sonne über dem Horizont), damit ist der erste
 * Vorzeichenwechsel von rayY − elevation der physikalisch richtige Auftreffpunkt.
 */

/** Marsch-Schrittweite entlang der Schattenrichtung (Weltmeter). */
export const MARCH_STEP_M = 0.5;
/** Ziel-Auflösung der Silhouetten-Subdivision (Weltmeter, Plan-Checkpoint). */
export const SUBDIVISION_TARGET_M = 0.5;
/** Deckel Subdivisions-Segmente pro Silhouetten-Kante (Polygonlast). */
export const MAX_SEGMENTS_PER_EDGE = 24;
/** Bisektions-Iterationen nach dem ersten Treffer (< 5 cm Genauigkeit). */
const BISECTION_ITERATIONS = 4;
/** Unter diesem Horizontalanteil gilt die Sonne als Zenit (Schatten = Footprint). */
const MIN_HORIZONTAL = 1e-9;
/** Toleranz für „liegt auf demselben Niveau" (Fast-Path-Probe, Meter). */
export const LEVEL_EPS_M = 0.01;

/**
 * Horizontale Schattenrichtung + Fallrate des Sonnenstrahls.
 *
 * @param {{x:number,y:number,z:number}} sunDirection - Einheitsvektor ZUR Sonne
 * @returns {{dx:number,dz:number,dropPerMeter:number,horizontal:number}|null}
 *   null bei Zenit (kein horizontaler Anteil) — Schatten fällt senkrecht.
 */
export function shadowRay(sunDirection) {
  const horizontal = Math.hypot(sunDirection.x, sunDirection.z);
  if (horizontal < MIN_HORIZONTAL) return null;
  return {
    dx: -sunDirection.x / horizontal,
    dz: -sunDirection.z / horizontal,
    dropPerMeter: sunDirection.y / horizontal,
    horizontal,
  };
}

/**
 * First-Hit eines Silhouetten-Punkts gegen die Terrain-Oberfläche.
 *
 * @param {{x:number,z:number}} point - Fußpunkt (Weltmeter, xz)
 * @param {number} yAbs - absolute Höhe des Silhouetten-Punkts (Meter)
 * @param {object} ray - aus shadowRay(); null ⇒ Zenit
 * @param {{elevationAt:Function, minLevel:number}} terrain
 * @returns {{x:number,z:number}} Auftreffpunkt auf der Oberfläche
 */
export function projectPointToTerrain(point, yAbs, ray, terrain) {
  if (!ray) return { x: point.x, z: point.z };

  const { elevationAt, minLevel } = terrain;
  const posAt = (s) => ({ x: point.x + ray.dx * s, z: point.z + ray.dz * s });
  const rayY = (s) => yAbs - ray.dropPerMeter * s;

  // Punkt liegt auf/unter der Oberfläche → Schatten beginnt am Punkt selbst.
  if (yAbs <= elevationAt(point) + 1e-9) return { x: point.x, z: point.z };

  // Obergrenze: unter das globale Minimum kann der Strahl nicht sinnvoll fallen.
  const sMax = (yAbs - minLevel) / ray.dropPerMeter + MARCH_STEP_M;

  // Vorwärts-Marsch bis zum ersten Vorzeichenwechsel.
  let sBefore = 0;
  let sHit = null;
  for (let s = MARCH_STEP_M; s <= sMax; s += MARCH_STEP_M) {
    if (rayY(s) <= elevationAt(posAt(s))) {
      sHit = s;
      break;
    }
    sBefore = s;
  }
  if (sHit === null) {
    // Kein Treffer im Fenster (numerischer Randfall) — flacher Endpunkt.
    return posAt((yAbs - minLevel) / ray.dropPerMeter);
  }

  // Bisektion verfeinern.
  let lo = sBefore;
  let hi = sHit;
  for (let i = 0; i < BISECTION_ITERATIONS; i += 1) {
    const mid = (lo + hi) / 2;
    if (rayY(mid) <= elevationAt(posAt(mid))) hi = mid;
    else lo = mid;
  }
  return posAt(hi);
}

/**
 * Silhouetten-Ring subdividieren: pro Kante gleich viele Zwischenpunkte,
 * bemessen an der GESCHÄTZTEN projizierten Kantenlänge (damit Knicke an
 * Terrain-Kanten im Schattenrand auftauchen).
 *
 * @param {Array<{x:number,z:number}>} ring - Quell-Silhouette
 * @param {Array<{x:number,z:number}>} projectedCoarse - grob projizierte Ecken
 *   (gleiche Länge wie ring; liefert die Längenschätzung pro Kante)
 * @returns {Array<{x:number,z:number}>} subdividierter Quell-Ring
 */
export function subdivideRing(ring, projectedCoarse) {
  const out = [];
  const n = ring.length;
  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    const projLen = Math.hypot(
      projectedCoarse[j].x - projectedCoarse[i].x,
      projectedCoarse[j].z - projectedCoarse[i].z
    );
    const segments = Math.max(
      1,
      Math.min(MAX_SEGMENTS_PER_EDGE, Math.ceil(projLen / SUBDIVISION_TARGET_M))
    );
    for (let k = 0; k < segments; k += 1) {
      const t = k / segments;
      out.push({
        x: ring[i].x + (ring[j].x - ring[i].x) * t,
        z: ring[i].z + (ring[j].z - ring[i].z) * t,
      });
    }
  }
  return out;
}

/**
 * Fast-Path-Probe: liegen alle Punkte (innerhalb LEVEL_EPS_M) auf demselben
 * Niveau wie refLevel? Dann kreuzt der Schatten keine Terrain-Kante und die
 * flache Projektion (heutiger Code) ist exakt.
 */
export function pointsOnLevel(points, elevationAt, refLevel) {
  for (const p of points) {
    if (Math.abs(elevationAt(p) - refLevel) > LEVEL_EPS_M) return false;
  }
  return true;
}
